/**
 * Drawing a text box and a sticky note (SRS C4).
 *
 * Canvas draws one line of text where it is told and nothing else, so the words are
 * broken into lines first (text-layout.ts) and drawn one at a time here.
 *
 * Measuring is done at the CANVAS size and drawing at the screen size. Text metrics
 * scale with the font, so the lines break in exactly the same places at every zoom —
 * which is what stops a sentence rearranging itself as the teacher zooms in on it.
 */

import { canvasToScreen, type BoardObject, type Point, type Viewport } from '@opal/shared';

import { stickyEdge, stickyFill, stickyInk } from './sticky-colors.js';
import {
  estimateWidth,
  lineHeight,
  wrapLines,
  type MeasureText,
} from './text-layout.js';
import {
  BOLD_WEIGHT,
  contentOf,
  fontSizeOf,
  paddingOf,
  stickyDataOf,
  textDataOf,
  wrapWidthOf,
} from './text-object.js';

const FONT_TOKEN = '--font-ui';
const FONT_FALLBACK = "'Inter', system-ui, sans-serif";

let family: string | null = null;

/** The interface font, read from the stylesheet once. */
export function fontFamily(): string {
  if (family !== null) return family;
  if (typeof document === 'undefined') return FONT_FALLBACK;
  const value = getComputedStyle(document.documentElement).getPropertyValue(FONT_TOKEN).trim();
  family = value === '' ? FONT_FALLBACK : value;
  return family;
}

/** The CSS font shorthand for words of this size, in whatever unit the size is in. */
export function fontOf(
  size: number,
  weight: number,
  italic: boolean,
): string {
  return `${italic ? 'italic ' : ''}${weight} ${size}px ${fontFamily()}`;
}

let scratch: CanvasRenderingContext2D | null | undefined;

/** A context kept aside purely for measuring, so no visible canvas is disturbed. */
function scratchContext(): CanvasRenderingContext2D | null {
  if (scratch !== undefined) return scratch;
  scratch =
    typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
  return scratch;
}

/**
 * A ruler for this object's words, in canvas units.
 *
 * Falls back to a rough estimate where there is no canvas to measure with — a test,
 * or the moment before the first paint. The box may then be a little too tall or
 * short until it is next drawn, which is better than not laying out at all.
 */
export function measurerFor(object: BoardObject): MeasureText {
  const size = fontSizeOf(object);
  const text = textDataOf(object);
  const ctx = scratchContext();
  if (!ctx) return (value) => estimateWidth(value, size);
  ctx.font = fontOf(size, object.kind === 'sticky' ? 400 : text.fontWeight, text.italic);
  return (value) => ctx.measureText(value).width;
}

/** The lines this object's words fill, at its current width. */
export function linesOf(object: BoardObject): string[] {
  return wrapLines(contentOf(object), wrapWidthOf(object), measurerFor(object));
}

interface Placement {
  /** Where the words start across the box, in screen pixels from its left edge. */
  readonly x: number;
  readonly align: CanvasTextAlign;
}

function placementOf(align: string, width: number, padding: number): Placement {
  if (align === 'center') return { x: width / 2, align: 'center' };
  if (align === 'right') return { x: width - padding, align: 'right' };
  return { x: padding, align: 'left' };
}

/**
 * Puts the canvas into the object's own frame: the middle of the box at the origin,
 * turned by however much the box is turned, and measured in screen pixels.
 */
function enterBox(
  ctx: CanvasRenderingContext2D,
  object: BoardObject,
  viewport: Viewport,
): { readonly width: number; readonly height: number } {
  const centre: Point = {
    x: object.x + object.width / 2,
    y: object.y + object.height / 2,
  };
  const at = canvasToScreen(viewport, centre);
  ctx.save();
  ctx.translate(at.x, at.y);
  if (object.rotation !== 0) ctx.rotate(object.rotation);
  return { width: object.width * viewport.zoom, height: object.height * viewport.zoom };
}

/** The paper a sticky note is written on, with a faint edge so a pale note is visible. */
function paintPaper(
  ctx: CanvasRenderingContext2D,
  object: BoardObject,
  width: number,
  height: number,
): void {
  ctx.fillStyle = stickyFill(stickyDataOf(object).color);
  ctx.fillRect(-width / 2, -height / 2, width, height);
  ctx.strokeStyle = stickyEdge();
  ctx.lineWidth = 1;
  ctx.strokeRect(-width / 2, -height / 2, width, height);
}

/** Draws the words, line by line, from the top of the box down. */
function paintLines(
  ctx: CanvasRenderingContext2D,
  object: BoardObject,
  size: { readonly width: number; readonly height: number },
  viewport: Viewport,
): void {
  const lines = linesOf(object);
  if (lines.length === 0) return;

  const sticky = object.kind === 'sticky';
  const text = textDataOf(object);
  const canvasSize = fontSizeOf(object);
  const padding = paddingOf(object) * viewport.zoom;
  const step = lineHeight(canvasSize) * viewport.zoom;
  const place = placementOf(sticky ? 'left' : text.align, size.width, padding);

  ctx.fillStyle = sticky ? stickyInk() : text.color;
  ctx.font = fontOf(
    canvasSize * viewport.zoom,
    sticky ? 400 : text.fontWeight,
    sticky ? false : text.italic,
  );
  ctx.textAlign = place.align;
  ctx.textBaseline = 'top';

  const left = -size.width / 2;
  const top = -size.height / 2 + padding;
  lines.forEach((line, index) => {
    ctx.fillText(line, left + place.x, top + index * step);
  });
}

/**
 * Draws a text box or a sticky note.
 *
 * Words are kept inside their box: a note typed full to overflowing shows what fits
 * rather than spilling across whatever is beside it. A text box grows instead
 * (text-object.ts), so nothing is ever lost from one.
 */
export function paintTextObject(
  ctx: CanvasRenderingContext2D,
  object: BoardObject,
  viewport: Viewport,
): void {
  const size = enterBox(ctx, object, viewport);
  if (object.kind === 'sticky') paintPaper(ctx, object, size.width, size.height);
  ctx.beginPath();
  ctx.rect(-size.width / 2, -size.height / 2, size.width, size.height);
  ctx.clip();
  paintLines(ctx, object, size, viewport);
  ctx.restore();
}

/** The weight a text box is written at, as the bold switch sees it. */
export function isBold(object: BoardObject): boolean {
  return textDataOf(object).fontWeight >= BOLD_WEIGHT;
}
